<template>
  <div class="match-timeline">
    <div v-if="items.length === 0" class="tl-empty">{{ $t('statistics.noevents') }}</div>

    <template v-else>
      <template v-for="row in rows">
        <div v-if="row.kind === 'separator'" :key="row.key" class="tl-separator">
          <span>{{ row.text }}</span>
        </div>

        <div v-else :key="row.key" class="tl-row" :class="row.side === '2' ? 'is-away' : 'is-home'">
          <div class="tl-side tl-side--home">
            <template v-if="row.side !== '2'">
              <div class="tl-text">
                <div class="tl-label">{{ row.label }}</div>
                <div v-if="row.sub" class="tl-sub">{{ row.sub }}</div>
              </div>
              <span class="tl-icon" :style="{ '--tl-c': row.color }">
                <img v-if="row.img" :src="row.img" class="tl-svg" alt="" />
                <span v-else-if="row.svg" class="tl-svg" v-html="svgs[row.svg]"></span>
                <v-icon v-else size="12" :color="row.color">{{ row.icon }}</v-icon>
              </span>
            </template>
          </div>

          <div class="tl-time">{{ row.time }}</div>

          <div class="tl-side tl-side--away">
            <template v-if="row.side === '2'">
              <span class="tl-icon" :style="{ '--tl-c': row.color }">
                <img v-if="row.img" :src="row.img" class="tl-svg" alt="" />
                <span v-else-if="row.svg" class="tl-svg" v-html="svgs[row.svg]"></span>
                <v-icon v-else size="12" :color="row.color">{{ row.icon }}</v-icon>
              </span>
              <div class="tl-text">
                <div class="tl-label">{{ row.label }}</div>
                <div v-if="row.sub" class="tl-sub">{{ row.sub }}</div>
              </div>
            </template>
          </div>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import {FULL_MAPPING, PERIODS_MAP} from "@/services/matchtracker/statNames";
import {typeOf, sideOf, incidentKey, collectIncidents, incidentPlayer, CARD_SVGS, GOAL_ICON} from "@/services/matchtracker/matchIncidents";
import {resolveFootballType} from "@/services/matchtracker/footballIncidents";

const GOAL_TYPES = [9, 24, 25];
const BASKET_IDS = ['48242', '1149105'];
const TENNIS_ID = '54094';
const FOOTBALL_ID = '6046';
const FOOTBALL_ICON = GOAL_ICON;
// Football keeps only the key moments: goals (own goals and penalty goals
// too), penalties awarded and missed, VAR, corners, cards, substitutions
// and injuries.
const FOOTBALL_TYPES = [...GOAL_TYPES, 8, 40, 1074, 1, 6, 7, 10, 2158, 2321];
const TABLE_TENNIS_ID = '265917';
// Numbered across the whole match ("3° Calcio d'angolo"), both teams together.
const ORDINAL_TYPES = { 1: 'statistics.corner', 6: 'statistics.yellowcard', 7: 'statistics.redcard', goal: 'statistics.goal' };
const ICONS = {
  1: { i: 'mdi-flag-variant', c: '#ef4444', t: 'statistics.corner' },
  2: { i: 'mdi-target', c: '#10b981', t: 'statistics.shotsontarget' },
  3: { i: 'mdi-arrow-top-right', c: '#94a3b8', t: 'statistics.shotsofftarget' },
  12: { i: 'mdi-whistle', c: '#94a3b8', t: 'statistics.fouls' },
  13: { i: 'mdi-shoe-cleat', c: '#3b82f6', t: 'statistics.freekicks' },
  14: { i: 'mdi-soccer-field', c: '#94a3b8', t: 'statistics.goalkicks' },
  15: { i: 'mdi-flag-off', c: '#facc15', t: 'statistics.offsides' },
  16: { i: 'mdi-shield', c: '#94a3b8', t: 'statistics.blockedshots' },
  17: { i: 'mdi-hand-ball', c: '#94a3b8', t: 'statistics.throwins' },
  18: { i: 'mdi-alert-circle-outline', c: '#f43f5e', t: 'statistics.woodwork' },
  31: { i: 'mdi-timer-outline', c: '#f59e0b', t: 'statistics.timeouts' },
  88: { i: 'mdi-shield-check', c: '#94a3b8', t: 'statistics.clearance' },
  96: { i: 'mdi-hand-back-left', c: '#00e5ff', t: 'statistics.save' },
  8: { i: 'mdi-bullseye-arrow', c: '#f59e0b', t: 'incidents.penalty' },
  40: { i: 'mdi-close-circle-outline', c: '#f43f5e', t: 'incidents.missedpenalty' },
  1074: { i: 'mdi-monitor-eye', c: '#38bdf8', t: 'incidents.var' },
  2158: { i: 'mdi-medical-bag', c: '#f43f5e', t: 'incidents.injury' },
  2321: { i: 'mdi-medical-bag', c: '#f43f5e', t: 'incidents.injury' }
};

// Drawn icons for the main incidents; the rest use MDI glyphs.
const SVGS = {
  goal: '<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="#fff"/><path fill="#1f2328" d="M12 7.6l3.3 2.4-1.26 3.9H9.96L8.7 10z"/><path fill="none" stroke="#1f2328" stroke-width="1.3" stroke-linejoin="round" d="M12 7.6V2.4M15.3 10l4.9-1.6M14.04 13.9l3 4.2M9.96 13.9l-3 4.2M8.7 10L3.8 8.4"/></svg>',
  yellow: CARD_SVGS.yellow,
  red: CARD_SVGS.red,
  corner: '<svg viewBox="0 0 24 24"><path d="M6 3v18" stroke="#e5e7eb" stroke-width="1.8" stroke-linecap="round"/><path d="M6.9 3.6c3-1.3 5.2 1.4 10.8.2l-2.6 4 2.6 4c-5.6 1.2-7.8-1.5-10.8-.2z" fill="#ef4444"/><path d="M3 21h6" stroke="#e5e7eb" stroke-width="1.8" stroke-linecap="round" opacity=".5"/></svg>',
  sub: '<svg viewBox="0 0 24 24"><path d="M8 20V6M4 10l4-4 4 4" fill="none" stroke="#22c55e" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/><path d="M16 4v14M12 14l4 4 4-4" fill="none" stroke="#ef4444" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
};

const resultsPair = results => {
  const val = pos => { const r = (results || []).find(x => String(x.Position || x.position) === pos); return r ? Number(r.Value ?? r.value ?? 0) : 0; };
  return { h: val('1'), a: val('2') };
};

// Match chronology: home incidents on the left of the centre line, away on
// the right, newest first, with the score after every finished period.
// Inside MatchTracker it gets the tracker's own incident list; standalone
// (mobile tab) it builds it from matchData, keeping the CurrentIncident
// values it sees go by.
export default {
  name: "MatchTimeline",
  props: {
    matchData: { type: Object, default: null },
    incidents: { type: Array, default: null },
    // live-scores/player-stats answer: player names, and who scored.
    playerStats: { type: Object, default: null }
  },
  data() {
    return {
      statNames: FULL_MAPPING,
      svgs: SVGS,
      liveIncidentsHistory: []
    };
  },
  watch: {
    currentIncident: {
      immediate: true,
      handler(latestInc) {
        if (this.incidents || !latestInc) return;
        const type = parseInt(latestInc.IncidentType || latestInc.id || 0);
        const uniqueId = latestInc.Id || `${type}-${latestInc.Seconds}`;
        if (type !== 0 && !this.liveIncidentsHistory.find(x => x.uniqueId === uniqueId)) {
          this.liveIncidentsHistory.push({ ...latestInc, uniqueId });
        }
      }
    }
  },
  methods: {
    // Football goals, checked against the scoreboard: the feed can list a
    // goal late, or only as a "Score" (27) record, while the scoreboard
    // already counts it. Per scoring team, real goal incidents always stay;
    // "Score" records and goals read from the score change (fromScore) fill
    // in only up to the scoreboard's count, and not within a few minutes of
    // a goal already listed.
    reconcileFootballGoals(list) {
      const sb = this.ls && (this.ls.Scoreboard || this.ls.scoreboard);
      const sbResults = (sb && (sb.Results || sb.results)) || [];
      const score = sbResults.length >= 2 ? resultsPair(sbResults) : null;
      const rankOf = inc => {
        const t = resolveFootballType(typeOf(inc)).type;
        if (inc.fromScore) return 2;
        if (t === 27) return 1;
        return GOAL_TYPES.includes(t) ? 0 : -1;
      };
      // An own goal counts for the other team.
      const scoringSide = inc => {
        const s = sideOf(inc);
        if (resolveFootballType(typeOf(inc)).type !== 24) return s;
        return s === '1' ? '2' : (s === '2' ? '1' : '');
      };
      const goals = list.filter(inc => rankOf(inc) >= 0).sort((a, b) => rankOf(a) - rankOf(b));
      const kept = { '1': [], '2': [] };
      const noSide = [];
      goals.forEach(inc => {
        const side = scoringSide(inc);
        const rank = rankOf(inc);
        if (!kept[side]) { if (rank === 0) noSide.push(inc); return; }
        if (rank > 0) {
          if (score && kept[side].length >= (side === '1' ? score.h : score.a)) return;
          if (kept[side].some(k => Math.abs(k.calculatedSeconds - inc.calculatedSeconds) < 180)) return;
        }
        kept[side].push(rank > 0 ? { ...inc, IncidentType: 9 } : inc);
      });
      return [...list.filter(inc => rankOf(inc) < 0), ...kept['1'], ...kept['2'], ...noSide]
          .sort((a, b) => a.calculatedSeconds - b.calculatedSeconds);
    },
    resolvePlayerName(inc, type = 'in') {
      if (!inc) return '';
      if (inc.Players || inc.players) {
        const playersObj = inc.Players || inc.players;
        if (type === 'in' && playersObj.Item1 && playersObj.Item1.length > 0) return playersObj.Item1[0].Name || playersObj.Item1[0].name || '';
        if (type === 'out' && playersObj.Item2 && playersObj.Item2.length > 0) return playersObj.Item2[0].Name || playersObj.Item2[0].name || '';
      }
      let name = type === 'in' ? (inc.PlayerName || inc.playerName) : (inc.PlayerOutName || inc.playerOutName);
      if (name) return name;
      const id = type === 'in' ? (inc.PlayerId || inc.playerId) : (inc.PlayerOutId || inc.playerOutId);
      if (id && String(id) !== "-1") return this.playerNames[String(id)] || '';
      return '';
    },
    // Goals whose incident names no one: the scorers the player-stats API
    // lists for that team, minus the goals already named. Used only when
    // what is left belongs to one player, so a name is never guessed.
    scorerPool(accepted) {
      const pool = { '1': {}, '2': {} };
      (this.playerStats?.teams || []).forEach(team => {
        const side = team.side === 'home' || String(team.position) === '1' ? '1' : team.side === 'away' || String(team.position) === '2' ? '2' : null;
        if (!side) return;
        (team.players || []).forEach(p => {
          const goals = parseInt(p.stats?.goals) || 0;
          if (goals && p.playerId) pool[side][String(p.playerId)] = { name: String(p.name || '').trim(), left: goals };
        });
      });
      accepted.forEach(({ inc, t }) => {
        if (!GOAL_TYPES.includes(t) || t === 24) return;
        const entry = pool[sideOf(inc)]?.[incidentPlayer(inc).id];
        if (entry) entry.left--;
      });
      return pool;
    },
    takeScorer(pool, side) {
      const left = Object.values(pool[side] || {}).filter(e => e.left > 0 && e.name);
      if (left.length !== 1) return '';
      left[0].left--;
      return left[0].name;
    },
    // What one incident shows: label, player line, icon.
    describe(inc, t, ordinal, fallbackName = '') {
      const pName = this.resolvePlayerName(inc, 'in') || fallbackName;
      const numbered = key => (ordinal ? `${ordinal}° ` : '') + this.$t(key);
      if (t === 10) {
        const pOut = this.resolvePlayerName(inc, 'out');
        // "Substitution", then who comes on (up) and who goes off (down).
        const sub = [pName ? `↑ ${pName}` : '', pOut ? `↓ ${pOut}` : ''].filter(Boolean).join(' · ');
        return { label: this.$t('incidents.substitution'), sub, svg: 'sub', color: '#a855f7' };
      }
      if (GOAL_TYPES.includes(t) || [27, 28, 30, 32].includes(t)) {
        let label;
        if (BASKET_IDS.includes(this.sportId)) {
          label = this.$t(t === 30 ? 'statistics.threepoints' : t === 28 ? 'statistics.twopoints' : t === 32 ? 'statistics.freethrows' : 'statistics.point');
        } else if (this.sportId === TENNIS_ID || this.sportId === TABLE_TENNIS_ID) {
          label = this.$t('statistics.point');
        } else {
          label = numbered('statistics.goal');
          if (t === 24) label += ` (${this.$t('statistics.owngoal')})`;
          if (t === 25) label += ` (${this.$t('statistics.penaltygoal')})`;
        }
        const assist = inc.AssistName || inc.assist1Name ||
            ((inc.AssistId || inc.assist1Id) ? this.resolvePlayerName({ PlayerId: inc.AssistId || inc.assist1Id }, 'in') : '');
        const res = resultsPair(inc.Results || inc.results);
        const hasScore = (inc.Results || inc.results || []).length >= 2;
        const sub = [pName, assist ? `${this.$t('statistics.assist')}: ${assist}` : '', hasScore ? `${res.h}-${res.a}` : ''].filter(Boolean).join(' · ');
        if (BASKET_IDS.includes(this.sportId)) return { label, sub, icon: 'mdi-basketball', color: '#f97316' };
        if (this.sportId === TENNIS_ID) return { label, sub, icon: 'mdi-tennis-ball', color: '#d9f99d' };
        if (this.sportId === TABLE_TENNIS_ID) return { label, sub, icon: 'mdi-table-tennis', color: '#fdba74' };
        if (this.sportId === FOOTBALL_ID) return { label, sub, img: FOOTBALL_ICON, color: '#22c55e' };
        return { label, sub, svg: 'goal', color: '#22c55e' };
      }
      if (t === 6 || t === 7) {
        return { label: numbered(ORDINAL_TYPES[t]), sub: pName, svg: t === 6 ? 'yellow' : 'red', color: t === 6 ? '#facc15' : '#ef4444' };
      }
      if (ICONS[t]) {
        return { label: ORDINAL_TYPES[t] ? numbered(ORDINAL_TYPES[t]) : this.$t(ICONS[t].t), sub: pName, svg: t === 1 ? 'corner' : null, icon: ICONS[t].i, color: ICONS[t].c };
      }
      return { label: this.$t(this.statNames[t] || 'statistics.event'), sub: pName, icon: 'mdi-circle-small', color: '#666' };
    }
  },
  computed: {
    ls() { return this.matchData?.Livescore || this.matchData?.livescore || this.matchData || null; },
    // Player id -> name, from the player-stats API, the event-detail squads
    // and the livescore player stats.
    playerNames() {
      const names = {};
      const add = (id, name) => {
        const n = String(name || '').trim();
        if (id !== undefined && id !== null && String(id) !== '-1' && n && !names[String(id)]) names[String(id)] = n;
      };
      (this.playerStats?.teams || []).forEach(t => (t.players || []).forEach(p => add(p.playerId, p.name)));
      (this.matchData?.competitors || []).forEach(c => (c.players || []).forEach(p => add(p.playerId || p.player?.id || p.id, p.player?.name || p.name || p.playerName)));
      const pStats = this.ls && (this.ls.PlayerStatistics || this.ls.playerStatistics) || [];
      (Array.isArray(pStats) ? pStats : []).forEach(p => add(p.PlayerId || p.playerId, p.PlayerName || p.playerName));
      return names;
    },
    currentIncident() { return this.ls ? (this.ls.CurrentIncident || this.ls.currentIncident || null) : null; },
    sportId() { return String(this.matchData?.Fixture?.Sport?.Id || this.matchData?.sportId || ''); },
    currentPeriod() {
      const sb = this.ls && (this.ls.Scoreboard || this.ls.scoreboard);
      return parseInt(sb?.CurrentPeriod ?? sb?.currentPeriod ?? 0) || 0;
    },
    timelineIncidents() {
      return this.incidents || this.ownIncidents;
    },
    ownIncidents() {
      return collectIncidents(this.ls, this.liveIncidentsHistory);
    },
    // The sport's periods that hold incidents, oldest first, and which period
    // each incident was listed in (quarters win over halves in basketball).
    periodBlocks() {
      const labels = PERIODS_MAP[this.sportId];
      const byIncident = {};
      const blocks = [];
      if (!labels || !this.ls) return { byIncident, blocks };
      const periods = [...(this.ls.Periods || this.ls.periods || [])]
          .map(p => ({ p, type: parseInt(p.Type ?? p.type) }))
          .filter(x => labels[x.type] && x.type !== 100)
          .sort((a, b) => a.type - b.type);
      periods.forEach(({ p, type }) => {
        let owns = false;
        (p.Incidents || p.incidents || []).forEach(i => {
          const key = incidentKey(i);
          if (!(key in byIncident)) { byIncident[key] = type; owns = true; }
        });
        if (owns || type === this.currentPeriod) blocks.push({ type, label: this.$t(labels[type]), results: resultsPair(p.Results || p.results) });
      });
      return { byIncident, blocks };
    },
    items() {
      const counters = {};
      const { byIncident, blocks } = this.periodBlocks;
      const fallbackPeriod = blocks.length ? blocks[blocks.length - 1].type : null;
      // Oldest first, so the ordinals count up with the match.
      let list = [...this.timelineIncidents].sort((a, b) => a.calculatedSeconds - b.calculatedSeconds);
      if (this.sportId === FOOTBALL_ID) list = this.reconcileFootballGoals(list);
      const accepted = [];
      const seenFootball = new Set();
      list.forEach(inc => {
        let t = typeOf(inc);
        if (isNaN(t) || [0, 4, 5, 11].includes(t)) return;
        if (this.sportId === FOOTBALL_ID) {
          // The feed sends goals under their kind too (header, right/left
          // foot, from outside the box, free kick, penalty): read them as the
          // main type, and list the same moment only once.
          t = resolveFootballType(t).type;
          if (!FOOTBALL_TYPES.includes(t)) return;
          const player = incidentPlayer(inc);
          const group = GOAL_TYPES.includes(t) ? 'goal' : t;
          const moment = `${group}-${Math.floor(inc.calculatedSeconds / 60)}-${sideOf(inc)}-${player.id || player.name}`;
          if (seenFootball.has(moment)) return;
          seenFootball.add(moment);
        }
        accepted.push({ inc, t });
      });
      const pool = this.sportId === FOOTBALL_ID ? this.scorerPool(accepted) : null;
      const items = [];
      accepted.forEach(({ inc, t }) => {
        const group = GOAL_TYPES.includes(t) ? 'goal' : t;
        const fallbackName = pool && group === 'goal' && t !== 24 && !this.resolvePlayerName(inc, 'in') ? this.takeScorer(pool, sideOf(inc)) : '';
        let ordinal = null;
        if (ORDINAL_TYPES[group]) ordinal = counters[group] = (counters[group] || 0) + 1;
        const key = incidentKey(inc);
        items.push({
          key: 'inc-' + key,
          group,
          side: sideOf(inc),
          time: `${Math.floor(inc.calculatedSeconds / 60)}'`,
          period: key in byIncident ? byIncident[key] : fallbackPeriod,
          ...this.describe(inc, t, ordinal, fallbackName)
        });
      });
      return items.reverse();
    },
    // Newest first, with "Score after <period>" closing every finished period.
    rows() {
      const { blocks } = this.periodBlocks;
      if (!blocks.length) return this.items;
      const scoreAfter = type => {
        const upTo = blocks.filter(b => b.type <= type);
        const h = upTo.reduce((s, b) => s + b.results.h, 0);
        const a = upTo.reduce((s, b) => s + b.results.a, 0);
        const block = blocks.find(b => b.type === type);
        return { kind: 'separator', key: 'sep-' + type, text: this.$t('statistics.scoreafter', { period: block.label, score: `${h}-${a}` }) };
      };
      const newestType = blocks[blocks.length - 1].type;
      const finished = type => type !== newestType || this.currentPeriod > type;
      const rows = [];
      let lastPeriod;
      this.items.forEach(item => {
        if (item.period !== lastPeriod && item.period !== null && finished(item.period)) rows.push(scoreAfter(item.period));
        lastPeriod = item.period;
        rows.push(item);
      });
      return rows;
    }
  }
}
</script>

<style scoped>
.match-timeline {
  padding: 8px 0 16px;
  color: var(--text-platform);
}

.tl-empty {
  text-align: center;
  padding: 24px 0;
  opacity: 0.6;
  font-size: var(--font-size-2xl);
}

.tl-row {
  display: grid;
  grid-template-columns: 1fr 42px 1fr;
  align-items: stretch;
  min-height: 30px;
}

.tl-side {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 10px;
  min-width: 0;
}

.tl-side--home {
  justify-content: flex-end;
  text-align: right;
}

.tl-side--away {
  justify-content: flex-start;
  text-align: left;
}

.tl-time {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--font-size-2xl);
  white-space: nowrap;
}

/* The centre line: white for home, primary for away, on the event's side. */
.tl-row.is-home .tl-time { border-left: 2px solid var(--text-platform); }
.tl-row.is-away .tl-time { border-right: 2px solid var(--color-primary); }

.tl-text {
  min-width: 0;
}

.tl-label {
  font-size: var(--font-size-2xl);
  line-height: 1.3;
}

.tl-sub {
  font-size: var(--font-size-lg);
  opacity: 0.6;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Icon chip, tinted with the incident colour. */
.tl-icon {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.06);
  background: color-mix(in srgb, var(--tl-c) 16%, transparent);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.06);
}

.tl-svg,
.tl-svg ::v-deep svg {
  display: block;
  width: 14px;
  height: 14px;
}

.tl-separator {
  display: flex;
  align-items: center;
  gap: 16px;
  margin: 16px 0;
  padding: 0 4px;
  font-size: var(--font-size-2xl);
  font-weight: 700;
  white-space: nowrap;
}

.tl-separator::before,
.tl-separator::after {
  content: '';
  flex: 1;
  height: 1px;
  background: rgba(255, 255, 255, 0.15);
}
</style>
